// components/CollapsiblePanel.jsx — panel yang bisa dilipat, dipakai bersama.
// ─────────────────────────────────────────────────────────────────────────────
// Pola ini pertama muncul inline di SapRouterLog.jsx. Begitu halaman kedua
// membutuhkannya, menyalinnya berarti dua salinan yang akan menyimpang: yang
// satu mengingat lipatan, yang lain tidak; yang satu memaksa panel terbuka saat
// isinya sedang aktif, yang lain lupa. Karena itu diangkat ke sini.
//
// KEPUTUSAN yang melekat pada pola ini:
//
//   1. Yang dilipat hanya ISI-nya. Judul beserta angka ringkasannya tetap
//      terlihat, supaya melipat tidak sama dengan menyembunyikan keberadaan
//      datanya. Panel yang tertutup harus tetap menjawab "ada berapa di dalam".
//
//   2. Status lipat disimpan di localStorage per panel. Panel yang harus
//      ditutup ulang setiap kali halaman dimuat justru menambah pekerjaan,
//      bukan mengurangi.
//
//   3. `forceOpen` mengalahkan status tersimpan. Dipakai ketika isi panel
//      sedang menentukan apa yang dilihat pengguna — mis. sebuah saringan
//      sedang aktif di dalamnya. Panel terlipat yang menyembunyikan saringan
//      aktif membuat pengguna melihat data tersaring tanpa tahu sebabnya, dan
//      itu jenis kebingungan yang paling mahal.
import React, { useState, useEffect, useCallback } from 'react';
import { ChevronRight } from 'lucide-react';

const KEY_PREFIX = 'panel.';

export function usePanelOpen(id, initial = true) {
  const key = KEY_PREFIX + id;
  const [open, setOpen] = useState(() => {
    try {
      const v = localStorage.getItem(key);
      if (v !== null) return v === '1';
    } catch { /* private mode: jatuh ke nilai awal */ }
    return initial;
  });
  useEffect(() => {
    try { localStorage.setItem(key, open ? '1' : '0'); } catch { /* abaikan */ }
  }, [key, open]);
  return [open, setOpen];
}

export default function CollapsiblePanel({
  id, title, meta, note, defaultOpen = true, forceOpen = false,
  right = null, children,
}) {
  const [stored, setStored] = usePanelOpen(id, defaultOpen);
  const open = forceOpen || stored;
  const toggle = useCallback(() => setStored(o => !o), [setStored]);

  return (
    <div style={{ marginBottom: open ? 20 : 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 8px' }}>
        <div
          role="button" tabIndex={0} aria-expanded={open}
          onClick={toggle}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
          }}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
            cursor: 'pointer', userSelect: 'none' }}>
          <ChevronRight size={15} color="#64748b" style={{ flexShrink: 0,
            transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s ease' }} />
          <h2 style={{ fontSize: 14.5, fontWeight: 700, color: '#0f172a', margin: 0 }}>
            {title}
            {meta != null && meta !== ''
              ? <span style={{ fontSize: 11.5, fontWeight: 400, color: '#94a3b8' }}> — {meta}</span>
              : null}
          </h2>
        </div>
        {/* Kontrol yang harus tetap terjangkau saat panel tertutup diletakkan
            di sini, DI LUAR area klik lipat — mis. tombol melepas saringan. */}
        {right}
      </div>
      {open ? (
        <>
          {note ? (
            <div style={{ fontSize: 11.5, color: '#94a3b8', lineHeight: 1.6,
              margin: '0 0 8px', maxWidth: 860 }}>{note}</div>
          ) : null}
          {children}
        </>
      ) : null}
    </div>
  );
}
